'use client'
import { Button } from '@/components/Button'
import { Popup } from '@/components/Popup'
import { RadioButton } from '@/components/RadioButton'
import { BRANDSIDE_SERVICE_URL } from '@/config/site'
import { usePopup } from '@/hooks/Popup'
import { Tariff } from '@/services/Api/generated'
import { Group } from '@/shared/Calculator/components/Group'
import { Price } from '@/shared/Calculator/components/Price'
import { TariffList } from '@/shared/Calculator/components/TariffList'
import { TextPopup } from '@/shared/Calculator/components/TextPopup'
import { TypesTrademark } from '@/shared/Calculator/components/TypesTrademark'
import { MktuClassButton } from '@/shared/Mktu/components/MktuClassButton'
import {
  RegistrationPeriod,
  RegistrationPeriodNameMap,
  TrademarkType,
  TrademarkTypeNameMap,
} from '@/store/Calculator'
import { Title } from '@/domains/WebApp/components/Title'
import { calculatorStore } from '@/domains/WebApp/store/Calculator'
import { mktuStore } from '@/domains/WebApp/store/Mktu'
import cn from 'classnames'
import { observer } from 'mobx-react-lite'
import { FC, useCallback, useLayoutEffect } from 'react'
import styles from './Calculator.module.scss'

interface CalculatorProps {
  className?: string
}
export const Calculator: FC<CalculatorProps> = observer(({ className }) => {
  const controlTariffListPopup = usePopup(false, true)
  const controlTrademarkPopup = usePopup(false, true)
  const controlCountClassesPopup = usePopup(false, true)
  const controlRegistrationPeriodPopup = usePopup(false, true)
  const controlCertificateFormatPopup = usePopup(false, true)

  const selectTariffAndClosePopup = useCallback(
    (tariff: Tariff) => {
      calculatorStore.setTariff(tariff)
      controlTariffListPopup.hide()
    },
    [controlTariffListPopup]
  )

  const selectTrademarkAndClosePopup = useCallback(
    (trademark: TrademarkType) => {
      calculatorStore.setTrademarkType(trademark)
      controlTrademarkPopup.hide()
    },
    [controlTrademarkPopup]
  )

  useLayoutEffect(() => {
    calculatorStore.setSelectedMktuClasses(mktuStore.selectedMktuClasses)
  }, [])

  return (
    <div className={cn(styles.container, className)}>
      <Title
        className={styles.title}
        title="Калькулятор стоимости регистрации"
      />
      <div className={styles.content}>
        <div className={styles.form}>
          <Group
            number="1"
            title="Выберите тариф"
            bodyClassName={styles.checkboxes}
            onShowTooltip={controlTariffListPopup.show}
          >
            <RadioButton
              name="tariff"
              value={Tariff.Self}
              onChange={calculatorStore.setTariff}
              checked={calculatorStore.tariff === Tariff.Self}
              label="Тариф “Удобный для самостоятельных”"
            />
            <RadioButton
              name="tariff"
              value={Tariff.Optimal}
              onChange={calculatorStore.setTariff}
              checked={calculatorStore.tariff === Tariff.Optimal}
              label="Тариф “Оптимальный для уверенных”"
            />
            <RadioButton
              name="tariff"
              value={Tariff.AllInclusive}
              onChange={calculatorStore.setTariff}
              checked={calculatorStore.tariff === Tariff.AllInclusive}
              label="Тариф “Все включено”"
            />
            <RadioButton
              name="tariff"
              value={Tariff.WithGuarantee}
              onChange={calculatorStore.setTariff}
              checked={calculatorStore.tariff === Tariff.WithGuarantee}
              label="Тариф “С гарантией”"
            />
          </Group>
          <Group
            number="2"
            title="Выберите вид товарного знака"
            bodyClassName={styles.checkboxes}
            onShowTooltip={controlTrademarkPopup.show}
          >
            <RadioButton
              name="trademarkType"
              value={TrademarkType.WORD}
              onChange={calculatorStore.setTrademarkType}
              checked={calculatorStore.trademarkType === TrademarkType.WORD}
              label={TrademarkTypeNameMap.WORD}
            />
            <RadioButton
              name="trademarkType"
              value={TrademarkType.IMAGE}
              onChange={calculatorStore.setTrademarkType}
              checked={calculatorStore.trademarkType === TrademarkType.IMAGE}
              label={TrademarkTypeNameMap.IMAGE}
            />
            <RadioButton
              name="trademarkType"
              value={TrademarkType.COMBINE}
              onChange={calculatorStore.setTrademarkType}
              checked={calculatorStore.trademarkType === TrademarkType.COMBINE}
              label={TrademarkTypeNameMap.COMBINE}
            />
          </Group>
          <Group
            number="3"
            title="Количество классов МКТУ"
            onShowTooltip={controlCountClassesPopup.show}
          >
            <div className={styles.description}>
              Классы международной классификации товаров и услуг описывают
              области, к которым относятся разные товары и услуги. Мы можем
              подобрать нужные классы.
            </div>
            {calculatorStore.countClasses !== 0 ? (
              <div className={cn(styles.row, styles.mktuListContainer)}>
                <div>
                  <div className={styles.mktuTitle}>Классы МКТУ</div>
                  <div className={styles.mktuList}>
                    {calculatorStore.selectedClasses.map((mktuClass) => (
                      <MktuClassButton
                        key={mktuClass.uuid}
                        classNumber={mktuClass.number}
                      />
                    ))}
                  </div>
                </div>
              </div>
            ) : null}
            <div className={styles.row}>
              <Button href="/mktu" className={styles.mktuLink}>
                {calculatorStore.countClasses === 0
                  ? 'Подобрать классы МКТУ'
                  : 'Редактировать'}
              </Button>
            </div>
          </Group>
          <Group
            number="4"
            title="Выберите срок регистрации"
            bodyClassName={styles.checkboxes}
            onShowTooltip={controlRegistrationPeriodPopup.show}
          >
            <RadioButton
              name="registrationPeriod"
              value={RegistrationPeriod.STANDARD}
              onChange={calculatorStore.setRegistrationPeriod}
              checked={
                calculatorStore.registrationPeriod ===
                RegistrationPeriod.STANDARD
              }
              label={RegistrationPeriodNameMap.STANDARD}
            />
            <RadioButton
              name="registrationPeriod"
              value={RegistrationPeriod.ACCELERATED}
              onChange={calculatorStore.setRegistrationPeriod}
              checked={
                calculatorStore.registrationPeriod ===
                RegistrationPeriod.ACCELERATED
              }
              label={RegistrationPeriodNameMap.ACCELERATED}
            />
          </Group>
        </div>

        {/** RESULT */}
        <div className={styles.result}>
          <Price className={styles.price} store={calculatorStore} />
          <Button
            href={BRANDSIDE_SERVICE_URL ?? '#'}
            className={styles.sendRequest}
            target="_blank"
          >
            Подать заявку на регистрацию
          </Button>
        </div>
      </div>

      {controlTariffListPopup.isVisible ? (
        <Popup onClose={controlTariffListPopup.hide} className={styles.popup}>
          <div className={styles.popupContent}>
            <div className={styles.popupHeader}>
              <div className={styles.popupTitle}>Тарифы</div>
            </div>
            <TariffList onSelect={selectTariffAndClosePopup} />
          </div>
        </Popup>
      ) : null}

      {controlTrademarkPopup.isVisible ? (
        <Popup onClose={controlTrademarkPopup.hide} className={styles.popup}>
          <div className={styles.popupContent}>
            <div className={styles.popupHeader}>
              <div className={styles.popupTitle}>Вид товарного знака</div>
            </div>
            <TypesTrademark onSelect={selectTrademarkAndClosePopup} />
          </div>
        </Popup>
      ) : null}

      <TextPopup
        title="Количество классов МКТУ"
        text={
          <>
            <p>
              МКТУ (Международная классификация товаров и услуг) - это,
              классификация товаров и услуг, используемая для регистрации
              товарных знаков.
            </p>
            <p>
              Классификация построена по принципу однотипности товаров и услуг.
            </p>
            <p>
              МКТУ содержит всего 45 классов: 34 класса для товаров (классы с
              1‑го по 34‑й включительно) и 11 классов для услуг (классы с 35‑го
              по 45-й включительно).
            </p>
            <p>
              Важно регистрировать товарный знак во всех классах, в которых Вы
              планируете вести коммерческую деятельность.
            </p>
            <p>
              Обратите внимание, что от количества выбранных классов зависит
              размер госпошлины за регистрацию товарного знака. Чтобы
              сэкономить, выбирайте только те классы, в которых бизнесу реально
              требуется защита интеллектуальной собственности.
            </p>
          </>
        }
        isVisible={controlCountClassesPopup.isVisible}
        onClose={controlCountClassesPopup.hide}
      />

      <TextPopup
        title="Какой бывает срок регистрации?"
        text={
          <>
            <p>
              Стандартный срок регистрации товарного знака в Роспатенте: от 6 до
              12 месяцев.
            </p>
            <p>
              Срок регистрации в Роспатенте можно ускорить до 2-х месяцев, но
              для этого необходимо будет заплатить дополнительную госпошлину в
              Роспатент за ускорение сроков экспертизы:
            </p>
            <ul>
              <li>для словесных и изобразительных знаков – 94 400 руб.</li>
              <li>для комбинированных знаков – 188 800 руб.</li>
            </ul>
          </>
        }
        isVisible={controlRegistrationPeriodPopup.isVisible}
        onClose={controlRegistrationPeriodPopup.hide}
      />

      <TextPopup
        title="Форматы получения свидетельства"
        text="МКТУ – это сокращенное название Международной классификации товаров и услуг (International (Nice) Classification of Goods and Services for the Purposes of the Registration of Marks), учрежденной Соглашением о классификациях (Ниццкое соглашение), предназначенной для регистрации товарных знаков"
        isVisible={controlCertificateFormatPopup.isVisible}
        onClose={controlCertificateFormatPopup.hide}
      />
    </div>
  )
})
